import { ReactElement, ReactNode, useCallback, useMemo } from 'react'
import { useController, useFormContext, useWatch } from 'react-hook-form'
import { Box, Checkbox, FormControlLabel, Tooltip, useTheme } from '@mui/material'
import { SuggestingTextField } from '@ui2/forms'
import { MaterialSymbol } from '@ui2/icons'
import { useResultsStore } from '@editorStores'
import {
  PREAMBLE_COMMENT_SUGGESTIONS,
  POSITION_LIST_COMMENT_SUGGESTIONS,
  withUserCommentSuggestion,
} from '../../../commentSuggestions'

const CommentField = ({
  name,
  label,
  placeholder,
  icon = 'description',
  suggestions,
  onFieldBlur,
  disableForm = false,
  footer,
}: {
  name: string
  label: string
  placeholder: string
  icon?: string
  suggestions: string[]
  onFieldBlur?: (value: string) => void
  disableForm?: boolean
  footer?: ReactNode
}): ReactElement => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Box
        component="label"
        sx={{
          fontSize: 11,
          color: theme.palette.text.secondary,
          fontFamily: 'Roboto',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        {label}
        <MaterialSymbol icon={icon} size={14} sx={{ color: 'rgba(0,0,0,0.38)' }} />
      </Box>
      <SuggestingTextField
        name={name}
        placeholder={placeholder}
        suggestions={suggestions}
        disabled={disableForm}
        openOnFocus
        onFieldBlur={onFieldBlur}
      />
      {footer}
    </Box>
  )
}

const CommentsSection = ({
  disableForm = false,
  isChildElement = false,
}: {
  disableForm?: boolean
  isChildElement?: boolean
}): ReactElement => {
  const { control } = useFormContext()
  const commentsBase = 'elements.0.comments'
  const elementGuid = useWatch({
    name: 'elements.0.elementGuid',
  }) as string | undefined
  // The "Plan" checkbox is a form field; the linked Positionsplan comment is only created/deleted
  // when the Bemessung is saved (see the detail pane's submit → useReconcilePlanComment).
  const { field: showInPositionPlanField } = useController({
    control,
    name: `${commentsBase}.showInPositionPlan`,
  })
  const commentSuggestion = useResultsStore(state => state.commentSuggestion)
  const extendedCommentSuggestion = useResultsStore(state => state.extendedCommentSuggestion)
  const noteSuggestion = useResultsStore(state => state.noteSuggestion)
  const setCommentSuggestion = useResultsStore(state => state.setCommentSuggestion)
  const setExtendedCommentSuggestion = useResultsStore(state => state.setExtendedCommentSuggestion)
  const setNoteSuggestion = useResultsStore(state => state.setNoteSuggestion)

  const positionListSuggestions = useMemo(
    () => withUserCommentSuggestion(POSITION_LIST_COMMENT_SUGGESTIONS, commentSuggestion),
    [commentSuggestion],
  )
  const preliminaryRemarksSuggestions = useMemo(
    () => withUserCommentSuggestion(PREAMBLE_COMMENT_SUGGESTIONS, extendedCommentSuggestion),
    [extendedCommentSuggestion],
  )
  const noteSuggestions = useMemo(
    () => withUserCommentSuggestion([], noteSuggestion),
    [noteSuggestion],
  )
  const disablePositionGroupComments = disableForm || isChildElement

  // The linked Positionsplan comment's text is synced on save (detail-pane submit / element-list
  // autosave), not on blur — this handler only records the suggestion.
  const handlePositionListBlur = useCallback(
    (value: string) => {
      setCommentSuggestion(value.trim())
    },
    [setCommentSuggestion],
  )

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch', pt: 1 }}>
      <CommentField
        name={`${commentsBase}.positionList`}
        label="Kommentar Positionsliste"
        placeholder="Kommentar eingeben…"
        suggestions={positionListSuggestions}
        onFieldBlur={handlePositionListBlur}
        disableForm={disablePositionGroupComments}
        footer={
          <Tooltip
            title="Blendet den Positionslisten-Kommentar als verknüpften Kommentar am Element im Positionsplan ein bzw. aus. Die Änderung wird erst beim Speichern der Bemessung übernommen."
            placement="top"
            enterDelay={1000}
            enterNextDelay={1000}
          >
            {/* alignSelf keeps the tooltip trigger as wide as the checkbox + label instead of
                stretching across the full comment column (which triggered the tooltip on stray
                hovers and covered the text field). */}
            <FormControlLabel
              sx={{
                m: 0,
                alignSelf: 'flex-start',
                '& .MuiFormControlLabel-label': { fontSize: 12 },
              }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0.5 }}
                  checked={!!showInPositionPlanField.value}
                  disabled={disablePositionGroupComments || !elementGuid}
                  onChange={event => showInPositionPlanField.onChange(event.target.checked)}
                  data-cy="comment-plan-checkbox"
                />
              }
              label="Plan"
            />
          </Tooltip>
        }
      />
      <CommentField
        name={`${commentsBase}.preliminaryRemarks`}
        label="Vorbemerkungen"
        placeholder="Kommentar eingeben…"
        suggestions={preliminaryRemarksSuggestions}
        onFieldBlur={value => setExtendedCommentSuggestion(value.trim())}
        icon="sticky_note_2"
        disableForm={disablePositionGroupComments}
      />
      <CommentField
        name={`${commentsBase}.notes`}
        label="Notizen"
        icon="edit_note"
        placeholder="Notizen eingeben…"
        suggestions={noteSuggestions}
        onFieldBlur={value => setNoteSuggestion(value.trim())}
        disableForm={disableForm}
      />
    </Box>
  )
}

export default CommentsSection
